import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import BlogLayout from '@/components/admin/BlogLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Plus, Eye, Pencil, Trash2, FileText, Send, ExternalLink, LayoutGrid } from 'lucide-react';

interface Author {
  id: number;
  name: string;
  email: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
}

interface Post {
  id: number;
  title: string;
  slug: string;
  summary: string;
  cover_image: string;
  status: 'draft' | 'published' | 'archived';
  visibility: 'visible' | 'auth_only';
  published_at: string | null;
  created_at: string;
  updated_at: string;
  author: Author;
  categories: Category[];
  tags: Tag[];
}

interface PostListProps {
  posts: Post[];
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-yellow-100 text-yellow-800',
};

const statusLabels = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const visibilityLabels = {
  visible: 'Public',
  auth_only: 'Members Only',
};

const PostList: React.FC<PostListProps> = ({ posts, canCreate = true, canEdit = true, canDelete = false }) => {
  const [gridSize, setGridSize] = useState<'small' | 'medium' | 'large'>('medium');

  const gridSizeClasses = {
    small: 'grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
    medium: 'grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    large: 'grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3',
  };

  const handleDelete = (postId: number) => {
    if (confirm('Are you sure you want to delete this post?')) {
      router.delete(`/admin/blog/posts/${postId}`);
    }
  };

  const handlePublish = (postId: number) => {
    router.patch(`/admin/blog/posts/${postId}/publish`);
  };

  const handleUnpublish = (postId: number) => {
    router.patch(`/admin/blog/posts/${postId}/unpublish`);
  };

  const handleArchive = (postId: number) => {
    router.patch(`/admin/blog/posts/${postId}/archive`);
  };

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Blog', href: '/admin/blog/posts' },
    { label: 'Posts' },
  ];

  return (
    <BlogLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Posts</h1>
            <p className="text-muted-foreground">Manage your blog posts</p>
          </div>
          {canCreate && (
            <Button asChild>
              <Link href="/admin/blog/posts/new">
                <Plus className="mr-2 h-4 w-4" />
                New Post
              </Link>
            </Button>
          )}
          <div className="flex items-center gap-1 border rounded-md p-0.5">
            <Button
              variant={gridSize === 'small' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              onClick={() => setGridSize('small')}
              title="Small (5 columns)"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="ml-1 text-xs">S</span>
            </Button>
            <Button
              variant={gridSize === 'medium' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              onClick={() => setGridSize('medium')}
              title="Medium (4 columns)"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="ml-1 text-xs">M</span>
            </Button>
            <Button
              variant={gridSize === 'large' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              onClick={() => setGridSize('large')}
              title="Large (3 columns)"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="ml-1 text-xs">L</span>
            </Button>
          </div>
        </div>

        {posts.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
            <FileText className="h-12 w-12" />
            <p className="text-lg">No posts yet</p>
            {canCreate && (
              <Button variant="outline" size="sm" asChild>
                <Link href="/admin/blog/posts/new">Create your first post</Link>
              </Button>
            )}
          </div>
        ) : (
          <div className={`grid ${gridSizeClasses[gridSize]} ${gridSize === 'small' ? 'gap-3' : 'gap-4'}`}>
            {posts.map((post) => (
              <Card key={post.id} className="overflow-hidden flex flex-col">
                {/* Cover Image Thumbnail */}
                <div className="aspect-video w-full overflow-hidden bg-muted">
                  <img
                    src={post.cover_image || '/images/man-comp.webp'}
                    alt={post.title}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Card Header - Title and Status */}
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      href={`/admin/blog/posts/${post.id}`}
                      className="line-clamp-2 font-medium hover:underline"
                    >
                      {post.title}
                    </Link>
                    <Badge className={`shrink-0 ${statusColors[post.status]}`}>
                      {statusLabels[post.status]}
                    </Badge>
                  </div>
                </CardHeader>

                {/* Card Content - Excerpt */}
                <CardContent className="flex-1 p-4 pt-0">
                  <p className="line-clamp-2 text-sm text-muted-foreground">
                    {post.summary || 'No excerpt available'}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {post.visibility && (
                      <Badge variant="secondary" className="text-xs">
                        {visibilityLabels[post.visibility]}
                      </Badge>
                    )}
                    {post.categories.slice(0, 2).map((cat) => (
                      <Badge key={cat.id} variant="outline" className="text-xs">
                        {cat.name}
                      </Badge>
                    ))}
                    {post.categories.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{post.categories.length - 2}
                      </Badge>
                    )}
                  </div>
                </CardContent>

                {/* Card Footer - Actions */}
                <CardFooter className="flex items-center justify-between p-4 pt-0">
                  {/* Quick Action Buttons */}
                  <div className="flex items-center gap-1">
                    {/* View on Public Blog */}
                    {post.status === 'published' && (
                      <Button variant="ghost" size="icon" asChild>
                        <Link
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          title="View on website"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    )}
                    {/* Edit */}
                    {canEdit && (
                      <Button variant="ghost" size="icon" asChild>
                        <Link
                          href={`/admin/blog/posts/${post.id}/edit`}
                          title="Edit post"
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                    )}
                    {/* View Details */}
                    <Button variant="ghost" size="icon" asChild>
                      <Link
                        href={`/admin/blog/posts/${post.id}`}
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>

                  {/* Status Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {post.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handlePublish(post.id)}>
                          <Send className="mr-2 h-4 w-4" />
                          Publish
                        </DropdownMenuItem>
                      )}
                      {post.status === 'published' && (
                        <DropdownMenuItem onClick={() => handleUnpublish(post.id)}>
                          <FileText className="mr-2 h-4 w-4" />
                          Unpublish
                        </DropdownMenuItem>
                      )}
                      {post.status !== 'archived' && (
                        <DropdownMenuItem onClick={() => handleArchive(post.id)}>
                          <FileText className="mr-2 h-4 w-4" />
                          Archive
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(post.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </BlogLayout>
  );
};

export default PostList;
